import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { authService } from '@/services/authService';

type User = { username: string | null; roles: string[] } | null;

interface AuthContextType {
  user: User;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SILENT_REFRESH_INTERVAL_MS = 25 * 60 * 1000; // 25 minutes

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current !== null) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const startRefreshTimer = () => {
    clearRefreshTimer();
    refreshTimerRef.current = setInterval(async () => {
      const result = await authService.refreshSession();
      if (result.authenticated) {
        setUser({ username: result.user, roles: result.roles });
      } else {
        setUser(null);
        clearRefreshTimer();
      }
    }, SILENT_REFRESH_INTERVAL_MS);
  };

  useEffect(() => {
    const publicPaths = ['/login', '/auth/callback', '/access-denied'];
    const pathname = window.location.pathname.replace(/\/+$/, '');
    const basePath = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
    const routePath = basePath ? pathname.replace(basePath, '') || '/' : pathname;

    if (publicPaths.includes(routePath)) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const me = await authService.me();
        if (me.authenticated) {
          setUser({ username: me.user, roles: me.roles });
          startRefreshTimer();
        } else {
          // Session cookie expired — attempt silent refresh before giving up
          const refreshed = await authService.refreshSession();
          if (refreshed.authenticated) {
            setUser({ username: refreshed.user, roles: refreshed.roles });
            startRefreshTimer();
          } else {
            setUser(null);
          }
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();

    return () => clearRefreshTimer();
  }, []);

  const login = async () => {
    // get authorize URL from backend then redirect
    const data = await authService.authorize();
    if (data?.authorization_url) {
      window.location.href = data.authorization_url;
    }
  };

  const logout = async () => {
    clearRefreshTimer();
    const data = await authService.logout();
    setUser(null);
    // Redirect to GAS Single Logout if the backend provides the URL
    if (data?.logout_url) {
      window.location.href = data.logout_url;
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const me = await authService.me();
      if (me.authenticated) {
        setUser({ username: me.user, roles: me.roles });
        startRefreshTimer();
      } else {
        setUser(null);
        clearRefreshTimer();
      }
    } catch {
      setUser(null);
      clearRefreshTimer();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
